// Dependencies
import { useTranslation } from 'react-i18next';
import axios, { CancelTokenSource } from 'axios';
import { useSelector } from 'react-redux';
import React, { FC, ReactElement, useEffect, useState } from 'react';
// Core & Lab
import { Avatar, Chip } from '@mui/material';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import { getLocalizedValue } from '../../functions/getLocalizedValue';
// Types
import { CircuitCategory } from '../circuitCategory/objects/circuitCategory';
import { AppState } from '../../reducers';

let request_timeout: NodeJS.Timeout;
let cancelToken: CancelTokenSource;

type Props = {
    categories: CircuitCategory[],
    onChangeCategories: (categories: CircuitCategory[]) => void
}

const CircuitCategories: FC<Props> = (props): ReactElement => {
    const { t, i18n } = useTranslation();
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(false);
    const [options, setOptions] = useState<CircuitCategory[]>([]);
    const locale = useSelector((state: AppState) => state.locale.list?.find((item) => {
        return item.language_code === i18n.language;
    })?.id ?? 1);
    const quotationCode = useSelector((state: AppState) => state.header.quotation_code);
    const client_id = useSelector((state: AppState) => state.header.client_id);
    let type = useSelector((state: AppState) => state.circuit.type) ?? 'circuit';
    type = (type.trim().length > 0 ? type : 'circuit').toLowerCase();

    const onSearchChange = (event: { target: { value: React.SetStateAction<string>; }; }) => {
        setSearch(event.target.value);
    };
    const runRequest = () => {
        setLoading(true);
        if (typeof cancelToken != typeof undefined) {
            cancelToken.cancel("Operation canceled due to new request.");
        }
        cancelToken = axios.CancelToken.source();
        axios({
            method: 'GET',
            url: `${API_HREF}client/${client_id}/circuit-category/`,
            headers: {
                'Authorization': `Token ${localStorage.getItem('token')}`,
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                'Accept-Language': 'fr'
            },
            cancelToken: cancelToken.token,
            params: {
                search: search !== '' ? search : undefined
            }
        }).then((response) => {
            setOptions(response.data.results);
            setLoading(false);
        }).catch((error) => {
            if (!axios.isCancel(error)) {
                console.log('error : ', error);
                setLoading(false);
            }
        });
    };

    useEffect(() => {
        if (open) {
            clearTimeout(request_timeout);
            request_timeout = setTimeout(runRequest, 500);
        } else {
            setOptions([]);
        }
    }, [open, search]);

    return (
        <Autocomplete
            id={ 'circuit-category' }
            open={ open }
            onOpen={() => {
                setOpen(true);
                setSearch('');
            }}
            onClose={() => {
                setOpen(false);
            }}
            noOptionsText={ t('list.autocomplete_no_options_text') }
            loadingText={ t('list.autocomplete_loading_text') }
            clearOnBlur={ true }
            clearOnEscape={ true }
            isOptionEqualToValue={(option, value) => option.id === value.id}
            getOptionLabel={(option) => option.name}
            options={ options }
            loading={ loading }
            value={ props.categories ?? [] }
            disableCloseOnSelect
            onChange={(event, value) => {
                props.onChangeCategories(value);
            }}
            filterOptions={(x) => x}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={
                        t(
                            'shared.circuit-category-type',
                            { type: t(`shared.${type}`).toLowerCase() }
                        )
                    }
                    variant={ 'outlined' }
                    onChange={ onSearchChange }
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                { loading ? <CircularProgress color={ 'inherit' } size={ 20 }/> : null }
                                { params.InputProps.endAdornment }
                            </>
                        ),
                    }}
                    required={quotationCode === 'verdie'}
                />
            )}
            renderTags={(value, getTagProps) => value.map((category, index) => (
                <Chip
                    {...getTagProps({ index })}
                    key={category.id}
                    avatar={category.icon?.url ? <Avatar src={category.icon.url} /> : undefined}
                    label={getLocalizedValue(locale, category, 'name')}
                />
            ))}
            multiple
        />
    );
};

export default CircuitCategories;
